import { useDashboard } from '../hooks/useDashboard';
import { useSFTPStatus } from '../hooks/useSFTP';
import type { SFTPJob } from '@aigftpwatch/shared';
import { Card, CardHeader } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import {
  CubeIcon,
  TruckIcon,
  ArrowsRightLeftIcon,
  ServerStackIcon,
} from '@heroicons/react/24/outline';

export function DashboardPage() {
  const { data: dashboard, isLoading } = useDashboard();
  const { data: sftpStatus } = useSFTPStatus();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <svg className="h-8 w-8 animate-spin text-gold-500" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  const inv = dashboard?.inventory_summary ?? { gold_oz: 0, silver_oz: 0, platinum_oz: 0, palladium_oz: 0, total_items: 0 };
  const pending = dashboard?.pending_counts ?? { outshipments: 0, transfers: 0, new_accounts: 0 };

  const metals = [
    { name: 'Gold', oz: inv.gold_oz, color: 'text-gold-600 bg-gold-50 border-gold-200' },
    { name: 'Silver', oz: inv.silver_oz, color: 'text-silver-600 bg-silver-50 border-silver-200' },
    { name: 'Platinum', oz: inv.platinum_oz, color: 'text-blue-600 bg-blue-50 border-blue-200' },
    { name: 'Palladium', oz: inv.palladium_oz, color: 'text-purple-600 bg-purple-50 border-purple-200' },
  ];

  return (
    <div className="space-y-6">
      {/* Inventory Summary */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {metals.map((metal) => (
          <Card key={metal.name} className={`border ${metal.color}`}>
            <div className="flex items-center gap-3">
              <CubeIcon className="h-8 w-8 opacity-60" />
              <div>
                <p className="text-sm font-medium opacity-70">{metal.name}</p>
                <p className="text-2xl font-bold">{Number(metal.oz).toLocaleString()} oz</p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Pending Workflows */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card>
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-amber-100 p-2">
              <TruckIcon className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-silver-500">Pending Outshipments</p>
              <p className="text-2xl font-bold text-silver-900">{pending.outshipments}</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-blue-100 p-2">
              <ArrowsRightLeftIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-silver-500">Pending Transfers</p>
              <p className="text-2xl font-bold text-silver-900">{pending.transfers}</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-emerald-100 p-2">
              <ServerStackIcon className="h-6 w-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-silver-500">Pending New Accounts</p>
              <p className="text-2xl font-bold text-silver-900">{pending.new_accounts}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Recent SFTP Activity */}
      <Card padding={false}>
        <div className="p-6 pb-0">
          <CardHeader title="Recent SFTP Activity" description={`${sftpStatus?.pendingUploads ?? 0} pending uploads`} />
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-silver-200">
            <thead className="bg-silver-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-silver-500">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-silver-500">Direction</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-silver-500">File</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-silver-500">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-silver-500">Records</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-silver-500">Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-silver-200">
              {(sftpStatus?.recentJobs ?? []).slice(0, 10).map((job: SFTPJob) => (
                <tr key={job.id}>
                  <td className="px-4 py-3 text-sm text-silver-700">{job.job_type.replace(/_/g, ' ')}</td>
                  <td className="px-4 py-3 text-sm text-silver-700">{job.direction}</td>
                  <td className="px-4 py-3 text-sm font-mono text-silver-600">{job.file_name}</td>
                  <td className="px-4 py-3"><Badge>{job.status}</Badge></td>
                  <td className="px-4 py-3 text-sm text-silver-700">{job.record_count ?? '-'}</td>
                  <td className="px-4 py-3 text-sm text-silver-500">
                    {job.created_at ? new Date(job.created_at).toLocaleString() : '-'}
                  </td>
                </tr>
              ))}
              {(!sftpStatus?.recentJobs || sftpStatus.recentJobs.length === 0) && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-silver-500">
                    No recent SFTP activity
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Recent Activity */}
      <Card>
        <CardHeader title="Recent Activity" />
        <div className="space-y-3">
          {(dashboard?.recent_activity ?? []).slice(0, 8).map((entry: Record<string, unknown>) => (
            <div key={entry.id as number} className="flex items-center justify-between border-b border-silver-100 pb-3 last:border-0">
              <div>
                <p className="text-sm font-medium text-silver-800">
                  {String(entry.action).replace(/_/g, ' ')} - {String(entry.entity_type).replace(/_/g, ' ')} #{String(entry.entity_id)}
                </p>
                <p className="text-xs text-silver-500">{String(entry.user_email)}</p>
              </div>
              <p className="text-xs text-silver-400">
                {entry.timestamp ? new Date(String(entry.timestamp)).toLocaleString() : ''}
              </p>
            </div>
          ))}
          {(!dashboard?.recent_activity || dashboard.recent_activity.length === 0) && (
            <p className="py-4 text-center text-sm text-silver-500">No recent activity</p>
          )}
        </div>
      </Card>
    </div>
  );
}
